/**
 * Shared contract between the web app and the API: roles, password policy, and
 * request/response schemas. The API validates requests with these schemas; the
 * web app uses the same schemas for forms and the inferred types for data.
 */
import { z } from "zod";
// ---------- roles ----------
export const ROLES = ["super_admin", "supervisor", "paramedic"];
export const roleSchema = z.enum(ROLES);
export const ROLE_LABELS = {
    super_admin: "Super Admin",
    supervisor: "Supervisor",
    paramedic: "Paramedic",
};
// ---------- password policy (same rules as the legacy system) ----------
export const PASSWORD_RULES = [
    { label: "At least 8 characters", test: (p) => p.length >= 8 },
    { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
    { label: "One number", test: (p) => /[0-9]/.test(p) },
    { label: "One special character (!@#$%^&*())", test: (p) => /[!@#$%^&*()]/.test(p) },
];
/** Error message when the password fails any rule, or null when valid. */
export function validatePassword(password) {
    const failed = PASSWORD_RULES.filter((r) => !r.test(password));
    if (failed.length === 0)
        return null;
    return "Password must include: " + failed.map((r) => r.label.toLowerCase()).join(", ") + ".";
}
const uuid = z.string().uuid();
const trimmed = (max) => z.string().trim().max(max);
const optionalText = (max) => z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v ? v : undefined));
// ---------- hospitals ----------
export const hospitalInputSchema = z.object({
    name: trimmed(200).min(1, "Name is required"),
    code: optionalText(50),
    city: optionalText(100),
});
export const hospitalBulkSchema = z.object({
    hospitals: z.array(hospitalInputSchema.extend({ name: z.string().trim().max(200) })).max(2000),
});
// ---------- users ----------
export const emailSchema = z.string().trim().toLowerCase().email().max(254);
export const createUserSchema = z.object({
    email: emailSchema,
    /** Temporary password; the user must change it at first login. */
    password: z.string().min(8, "Password must be at least 8 characters").max(128),
    name: trimmed(120).optional(),
    role: roleSchema,
    badgeNo: optionalText(50),
    hospitalIds: z.array(uuid).max(200).optional(),
});
export const bulkUserRowSchema = z.object({
    email: z.string().trim(),
    password: z.string(),
    name: z.string().trim().optional(),
    role: z.string().trim().optional(),
    badgeNo: z.string().trim().optional(),
    /** Semicolon-separated hospital names (supervisors only). */
    hospitals: z.string().trim().optional(),
});
export const bulkUsersSchema = z.object({ users: z.array(bulkUserRowSchema).max(2000) });
export const updateUserMetaSchema = z.object({
    name: trimmed(120).optional(),
    badgeNo: z.string().trim().max(50).optional(),
});
export const setUserRoleSchema = z.object({ role: roleSchema });
export const assignHospitalsSchema = z.object({ hospitalIds: z.array(uuid).max(200) });
export const resetPasswordSchema = z.object({
    newPassword: z.string().min(8, "Password must be at least 8 characters").max(128),
});
export const updateProfileSchema = z.object({
    name: trimmed(120).optional(),
    badgeNo: z.string().trim().max(50).optional(),
});
/** Normalises free-text roles from CSV imports the way the legacy import did. */
export function normalizeRole(raw) {
    const r = (raw ?? "").trim().toLowerCase().replace(/[\s-]+/g, "_");
    if (r === "super_admin" || r === "superadmin")
        return "super_admin";
    if (r === "paramedic")
        return "paramedic";
    return "supervisor";
}
// ---------- checklist domain (form definitions, both revisions, columns, official form renderers) ----------
export * from "./checklist/checklistFields.js";
export * from "./checklist/checklistFieldsV1.js";
export * from "./checklist/checklistColumns.js";
export * from "./checklist/checklistColumnsV1.js";
export * from "./checklist/OfficialChecklistForm.js";
export * from "./checklist/OfficialChecklistFormV1.js";
export * from "./checklist/pdfAssets.js";
export * from "./naturalSort.js";
// ---------- PCR archive (closed module): field definitions, burn chart, official form ----------
export * from "./pcr/pcrFields.js";
export * from "./pcr/burnChart.js";
export * from "./pcr/OfficialPcrForm.js";
// ---------- Phase 5: API clients, webhooks, audit ----------
export * from "./integrations.js";
//# sourceMappingURL=index.js.map